import React, { useMemo } from 'react';

import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';

import { CheckmarkOutlinedIcon } from '@/assets/icons/tab/checkmark-outlined';
import { LobbyIcon } from '@/assets/icons/tab/home';
import { useSbkBetImport } from '@/feature/bet-share-sbk/hooks/use-sbk-bet-import';
import { PredictionAccessGate } from '@/feature/prediction-access/components/PredictionAccessGate';
import { useAppInit } from '@/hooks/use-app-init';
import { Product, useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { useOneLinkNavigation } from '@/hooks/use-one-link-navigation';

import { AccountTabConfig, TabBar, TabOptions, mapTabsFromTabSettings } from '../tabs';
import { EntriesStack } from './entriesStack';
import { LobbyStack } from './lobbyStack';
import { PredictionTabNavigatorParamList } from './types';

const TabPrediction = createBottomTabNavigator<PredictionTabNavigatorParamList>();

const PredictionTabsConfig = [
    { name: 'PredictionLobby', component: LobbyStack, icon: LobbyIcon, title: 'Home' },
    {
        name: 'PredictionEntries',
        component: EntriesStack,
        icon: CheckmarkOutlinedIcon,
        title: 'Track',
    },
] as const;

export const PredictionTabsNavigator = () => {
    useOneLinkNavigation();

    const tabSettings = useJurisdictionStore(state => state.jurisdictionSettings?.productConfig?.tabSettings);

    const tabsConfig = useMemo(() => {
        return mapTabsFromTabSettings([...PredictionTabsConfig, AccountTabConfig].filter(Boolean), tabSettings);
    }, [tabSettings]);

    useAppInit();

    return (
        <PredictionAccessGate>
            <SharedBetImporter />
            <TabPrediction.Navigator screenOptions={TabOptions} tabBar={TabBar}>
                {tabsConfig.map(tab => (
                    <TabPrediction.Screen
                        key={tab.name}
                        {...tab}
                        options={{ tabBarIcon: tab.icon, title: tab.title }}
                    />
                ))}
            </TabPrediction.Navigator>
        </PredictionAccessGate>
    );
};

// mounted inside the gate so shared bets only import once the user has cleared it
const SharedBetImporter = () => {
    useSbkBetImport(Product.Prediction);
    return null;
};
